import { Check, Loader2 } from 'lucide-react'
import type { AnalysisProgress as ProgressData } from '../lib/analyzer'

interface AnalysisProgressProps {
  progress: ProgressData
  fileName: string
}

const STEPS = [
  { id: 'load', label: 'Loading audio', minPercent: 0 },
  { id: 'sizzle', label: 'Analyzing sizzle', minPercent: 20 },
  { id: 'muddiness', label: 'Analyzing muddiness', minPercent: 35 },
  { id: 'clicks', label: 'Detecting clicks', minPercent: 50 },
  { id: 'dynamics', label: 'Measuring dynamics', minPercent: 65 },
  { id: 'lufs', label: 'Measuring loudness', minPercent: 80 },
  { id: 'report', label: 'Generating report', minPercent: 90 },
]

export function AnalysisProgress({ progress, fileName }: AnalysisProgressProps) {
  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header card */}
      <div className="forge-card p-6 mb-6 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-gradient-to-br from-[hsl(var(--forge-copper)/0.2)] to-[hsl(var(--forge-copper)/0.05)] border border-[hsl(var(--forge-copper)/0.3)]">
          <Loader2 className="w-8 h-8 text-forge-copper animate-spin" />
        </div>
        <p className="text-sm text-forge-muted mb-1">Analyzing</p>
        <p className="text-lg font-semibold text-white truncate max-w-[280px] mx-auto">{fileName}</p>
      </div>

      {/* Progress bar */}
      <div className="mb-8 progress-active">
        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${progress.percent}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-3">
          <p className="text-sm text-forge-muted">
            {progress.detail || progress.step}
          </p>
          <p className="text-sm font-mono text-forge-copper">
            {progress.percent}%
          </p>
        </div>
      </div>

      {/* Steps list */}
      <div className="forge-card p-4">
        <div className="space-y-1">
          {STEPS.map((step, index) => {
            const isComplete = progress.percent > step.minPercent + 10
            const isActive =
              progress.percent >= step.minPercent &&
              progress.percent <= step.minPercent + 15

            return (
              <div
                key={step.id}
                className={`
                  flex items-center gap-3 p-3 rounded-xl transition-all duration-300
                  ${isActive ? 'bg-[hsl(var(--forge-copper)/0.1)]' : ''}
                `}
              >
                {/* Step indicator */}
                <div
                  className={`
                    relative w-8 h-8 rounded-lg flex items-center justify-center
                    transition-all duration-300
                    ${isComplete
                      ? 'bg-[hsl(var(--forge-success))]'
                      : isActive
                        ? 'bg-[hsl(var(--forge-copper)/0.2)] border border-[hsl(var(--forge-copper)/0.5)]'
                        : 'bg-forge-surface'
                    }
                  `}
                >
                  {isComplete ? (
                    <Check className="w-4 h-4 text-white" />
                  ) : isActive ? (
                    <Loader2 className="w-4 h-4 text-forge-copper animate-spin" />
                  ) : (
                    <span className="text-xs font-mono text-forge-subtle">{index + 1}</span>
                  )}

                  {/* Glow effect for active step */}
                  {isActive && (
                    <div className="absolute inset-0 bg-[hsl(var(--forge-copper))] rounded-lg blur-lg opacity-30" />
                  )}
                </div>

                {/* Step label */}
                <span
                  className={`
                    text-sm font-medium transition-colors
                    ${isComplete
                      ? 'text-forge-muted'
                      : isActive
                        ? 'text-white'
                        : 'text-forge-subtle'
                    }
                  `}
                >
                  {step.label}
                </span>

                {/* Completion indicator */}
                {isComplete && (
                  <span className="ml-auto text-xs text-forge-success font-medium">Done</span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
